import * as React from 'react';

/**
 * Deferred-commit scheduler for auto-saving forms. Returns a `requestCommit`
 * callback that runs `commit` after the render that applied the triggering
 * edit, so the commit always diffs fresh state — wire it to text-input blurs
 * and select/checkbox/pick changes (never per keystroke). `commit` may be a
 * plain closure re-created each render; the latest one is always invoked.
 *
 * Commits are serialized: triggers that arrive while one is in flight coalesce
 * into a single re-run after it settles, re-reading fresh state — so consumers
 * can leave their inputs enabled during a save without racing mutations.
 *
 * The effect deliberately has no dependency array: it runs after every render,
 * and the tick guard makes every render where `requestCommit` wasn't called a
 * no-op.
 */
const useAutoCommit = (commit: () => void | Promise<void>): (() => void) => {
  const [commitTick, setCommitTick] = React.useState(0);
  const lastCommittedTickRef = React.useRef(0);
  const inFlightRef = React.useRef(false);
  const pendingRef = React.useRef(false);

  const requestCommit = React.useCallback(() => setCommitTick((n) => n + 1), []);

  React.useEffect(() => {
    if (commitTick === lastCommittedTickRef.current) {
      return;
    }
    lastCommittedTickRef.current = commitTick;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    void (async () => {
      try {
        await commit();
      } finally {
        inFlightRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          requestCommit();
        }
      }
    })();
  });

  return requestCommit;
};

export default useAutoCommit;
